import React, { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Footer from "../components/Footer";

export default function LoginPage() {
  const { t } = useTranslation();
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "signup" ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // signup-only fields
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [honeypot, setHoneypot] = useState(""); // hidden anti-bot field

  if (user) return <Navigate to="/home" replace />;

  const submitLogin = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/home");
    else setErr(res.error);
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    const currentLang = (typeof window !== "undefined" && window.localStorage.getItem("i18nextLng")) || "it";
    const shortLang = (currentLang || "").slice(0, 2).toLowerCase();
    const payload = {
      name, email, password,
      language: ["it", "de", "en"].includes(shortLang) ? shortLang : "it",
      honeypot: honeypot || null,
    };
    if (companyName.trim()) payload.company_name = companyName.trim();
    if (phone.trim()) payload.phone = phone.trim();
    const res = await register(payload);
    setLoading(false);
    // Success → route to /home; the top-level EmailVerifiedGate will show
    // the "check your email" screen because the account starts unverified.
    if (res.ok) navigate("/home");
    else setErr(res.error);
  };

  const switchMode = (next) => {
    setMode(next);
    setErr(null);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-1 flex flex-col md:flex-row">
      {/* Left: dark hero (Tesla-style contrast) */}
      <div className="relative md:w-1/2 bg-[#0A0A0B] text-white flex flex-col justify-between p-8 md:p-14 overflow-hidden min-h-[420px]">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-blue-600/25 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="relative z-10 flex items-center gap-3">
          <Link to="/" data-testid="login-back-to-landing" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
                <path d="M4 20 L12 4 L20 20 L17 20 L15.4 16 L8.6 16 L7 20 Z" fill="#0A0A0B" />
                <path d="M9.7 13 L12 8 L14.3 13 Z" fill="#2563EB" />
              </svg>
            </div>
            <div className="font-heading font-bold text-lg tracking-tight">AUTEXIO</div>
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative z-10"
        >
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400 mb-4">
            🇮🇹 &nbsp;·&nbsp; 🇩🇪 &nbsp;·&nbsp; Cross-border marketplace
          </p>
          <h1 className="font-heading font-bold tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
            L&apos;auto giusta.<br />
            Il margine giusto.<br />
            <span className="text-blue-400">In 10 secondi.</span>
          </h1>
          <p className="mt-6 text-slate-300 max-w-md leading-relaxed">
            {t("auth.login_subtitle")}
          </p>
        </motion.div>

        <div className="relative z-10 text-[11px] text-slate-500 tracking-wide">
          © 2025 AUTEXIO · Made for Italian & German dealers
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex flex-col">
        <div className="flex justify-end p-4 md:p-6">
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 pb-16">
          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="w-full max-w-md"
          >
            {mode === "login" ? (
              <>
                <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tighter text-slate-950">
                  {t("auth.login_title")}
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  {t("auth.welcome_back")}.
                </p>

                <form onSubmit={submitLogin} className="mt-8 space-y-4" data-testid="login-form">
                  <div>
                    <Label htmlFor="email" className="text-xs font-medium text-slate-600">
                      {t("auth.email")}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      autoComplete="email"
                      data-testid="login-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-blue-600"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-xs font-medium text-slate-600">
                      {t("auth.password")}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      autoComplete="current-password"
                      data-testid="login-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-blue-600"
                    />
                  </div>
                  {err && (
                    <div
                      data-testid="login-error"
                      className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                    >
                      {err}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="login-submit-button"
                    className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold tracking-tight shadow-sm"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.sign_in")}
                  </Button>
                  <p
                    data-testid="login-free-hint"
                    className="text-[11px] leading-snug text-center text-slate-500"
                  >
                    {t("pricing_hint.free_join_full")}
                  </p>
                </form>

                <div className="mt-8 pt-6 border-t border-slate-100 text-center">
                  <p className="text-[13px] text-slate-500">
                    {t("signup.prompt")}{" "}
                    <button
                      type="button"
                      data-testid="signup-link"
                      onClick={() => switchMode("signup")}
                      className="font-semibold text-blue-600 hover:text-blue-700 transition-colors underline-offset-4 hover:underline"
                    >
                      {t("signup.cta")}
                    </button>
                  </p>
                  <p className="mt-3 text-[12px] text-slate-500">
                    <a
                      href="/privati"
                      data-testid="login-privati-link"
                      className="hover:text-slate-900 transition-colors"
                    >
                      {t("privati.login_link")}
                    </a>
                  </p>
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="signup-back-to-login"
                  onClick={() => switchMode("login")}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 transition-colors mb-6"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  {t("signup.back_to_login")}
                </button>

                <p className="text-[11px] uppercase tracking-[0.24em] text-blue-600 font-semibold">
                  {t("signup.overline")}
                </p>
                <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tighter text-slate-950">
                  {t("signup.title")}
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  {t("signup.subtitle")}
                </p>

                <div
                  data-testid="signup-disclaimer"
                  className="mt-6 flex gap-3 rounded-xl border border-blue-100 bg-blue-50/60 p-3.5"
                >
                  <ShieldCheck className="w-4 h-4 flex-shrink-0 text-blue-600 mt-0.5" />
                  <p className="text-[12px] leading-snug text-slate-700">
                    {t("signup.disclaimer")}
                  </p>
                </div>

                <form onSubmit={submitSignup} className="mt-6 space-y-4" data-testid="signup-form">
                  {/* Honeypot: bots often fill hidden fields.
                      Real users leave it empty → silently accepted. */}
                  <div className="hidden" aria-hidden="true">
                    <label>
                      Website
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                      />
                    </label>
                  </div>
                  <div>
                    <Label htmlFor="su-name" className="text-xs font-medium text-slate-600">
                      {t("signup.name")}
                    </Label>
                    <Input
                      id="su-name"
                      type="text"
                      required
                      autoComplete="name"
                      data-testid="signup-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-blue-600"
                    />
                  </div>
                  <div>
                    <Label htmlFor="su-email" className="text-xs font-medium text-slate-600">
                      {t("signup.email")}
                    </Label>
                    <Input
                      id="su-email"
                      type="email"
                      required
                      autoComplete="email"
                      data-testid="signup-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-blue-600"
                    />
                  </div>
                  <div>
                    <Label htmlFor="su-password" className="text-xs font-medium text-slate-600">
                      {t("signup.password")}
                    </Label>
                    <Input
                      id="su-password"
                      type="password"
                      required
                      minLength={6}
                      autoComplete="new-password"
                      data-testid="signup-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-blue-600"
                    />
                    <p className="mt-1 text-[11px] text-slate-400">
                      {t("signup.password_hint")}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="su-company" className="text-xs font-medium text-slate-600">
                      {t("signup.company_name_optional")}
                    </Label>
                    <Input
                      id="su-company"
                      type="text"
                      autoComplete="organization"
                      data-testid="signup-company-input"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-blue-600"
                    />
                  </div>
                  <div>
                    <Label htmlFor="su-phone" className="text-xs font-medium text-slate-600">
                      {t("signup.phone_optional")}
                    </Label>
                    <Input
                      id="su-phone"
                      type="tel"
                      autoComplete="tel"
                      data-testid="signup-phone-input"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-blue-600"
                    />
                  </div>

                  {err && (
                    <div
                      data-testid="signup-error"
                      className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                    >
                      {err}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="signup-submit-button"
                    className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold tracking-tight shadow-sm"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        {t("signup.sending")}
                      </>
                    ) : (
                      t("signup.submit")
                    )}
                  </Button>
                  <p
                    data-testid="signup-free-hint"
                    className="text-[11px] leading-snug text-center text-slate-500"
                  >
                    {t("pricing_hint.free_join_full")}
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
}
